import { Container } from '@playcanvas/pcui';

import { Events } from '../events';
import { Scene } from '../scene';
import { solveBearingFromGcps } from './map-plot-bearing-solver';
import { MapPlotGroundPlaneGizmo, type MapPlotPlaneGizmoMode } from './map-plot-ground-plane-gizmo';

/** Max ground control points per alignment (minimum {@link MAP_PLOT_MIN_GCP_TO_SOLVE} required to compute fit). */
const MAP_PLOT_MAX_GCP_COUNT = 10;
const MAP_PLOT_MIN_GCP_TO_SOLVE = 3;

type Phase = 'closed' | 'plane' | 'gcps';

type GcpSnap = {
    id: string;
    splat: [number, number, number];
    lat: number;
    lng: number;
    ele?: number;
};

type FullSolveUi = {
    scale: number;
    residualMeters: number;
    gcpResidualsMeters: number[];
    headingDeg: number;
    basisNorth?: number[];
    basisUp?: number[];
    lat?: number;
    lng?: number;
};

function readMapPlotRmsOkM(): number {
    const w = window as unknown as {
        __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { mapPlotRmsOkM?: number };
    };
    const v = w.__VOLUGRAPH_SUPER_SPLAT_CONFIG__?.mapPlotRmsOkM;
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 2;
}

function readMapPlotConfig(): Record<string, unknown> | null {
    const w = window as unknown as {
        __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphMapPlotInitialState?: Record<string, unknown> };
    };
    const st = w.__VOLUGRAPH_SUPER_SPLAT_CONFIG__?.volugraphMapPlotInitialState;
    return st && typeof st === 'object' ? st : null;
}

/**
 * Volugraph “Map plot” guided workflow: ground plane → GCPs (north from ≥2 GCPs) → save to host.
 */
export class MapPlotWorkflow {
    private readonly events: Events;
    private readonly scene: Scene;
    private readonly canvasContainer: Container;
    private phase: Phase = 'closed';
    /** Portal host on document.body — carries data-volugraph-supersplat so index-embed.css applies to the panel. */
    private panelRoot: HTMLDivElement | null = null;
    private panel: HTMLDivElement | null = null;
    private gizmo: MapPlotGroundPlaneGizmo | null = null;

    private groundPlane: { normal: [number, number, number]; pointOnPlane: [number, number, number] } | null = null;
    /** Bearing stored for save/export; overwritten by GCP solver when ≥2 GCPs exist in GCP step. */
    private northDeg = 0;
    /** RMS quality of bearing fit (degrees), meaningful when ≥2 GCPs. */
    private bearingResidualDeg = 0;
    private gcps: GcpSnap[] = [];
    private lastFullSolve: FullSolveUi | null = null;

    /** One splat GCP at a time until map match + confirm or cancel (replace picked on canvas re-click, same #N). */
    private pendingGcpDraft: { n: number } | null = null;
    private nextGcpNumber = 1;
    private coordCardSetLatLng: ((lat: number, lng: number) => void) | null = null;
    /** Host mini-map forwards lat/lng only after user clicks Pick from map on the card. */
    private gcpMapPickArmed = false;
    /** User clicked “+ ADD GCP”; next splat click starts a draft (single-shot). */
    private gcpAddArmed = false;

    private unsubRfi: (() => void) | null = null;
    private readonly boundLayoutMapPlotPanel = (): void => {
        this.layoutMapPlotPanel();
    };

    constructor(events: Events, scene: Scene, canvasContainer: Container) {
        this.events = events;
        this.scene = scene;
        this.canvasContainer = canvasContainer;

        events.on('volugraph.mapPlot.toggle', () => {
            this.toggle();
        });

        events.on('volugraph.mapPlot.open', () => {
            if (this.phase === 'closed') {
                this.hydrateFromConfigIfClosed();
                this.open();
            }
        });

        events.on('volugraph.splatLoaded', () => {
            this.hydrateFromConfigIfClosed();
        });

        events.on('volugraph.mapPlot.configBumped', () => {
            if (this.phase === 'closed') {
                this.hydrateFromConfigIfClosed();
            }
        });

        try {
            this.events.removeFunction('volugraph.mapPlot.export');
        } catch {
            /* ignore */
        }
        this.events.function('volugraph.mapPlot.export', () => this.exportState());

        const reg = () => {
            try {
                this.events.removeFunction('coord.canStartNewMarker');
            } catch {
                /* ignore */
            }
            this.events.function('coord.canStartNewMarker', () => true);

            try {
                this.events.removeFunction('mapPlot.allocGcpNumber');
            } catch {
                /* ignore */
            }
            this.events.function('mapPlot.allocGcpNumber', () => {
                if (this.phase !== 'gcps') return null;
                this.hydrateGcpsFromCoordExportIfEmpty();
                if (this.pendingGcpDraft !== null) return this.pendingGcpDraft.n;
                return this.nextGcpNumber;
            });

            try {
                this.events.removeFunction('volugraph.mapPlot.registerCoordCardSetter');
            } catch {
                /* ignore */
            }
            this.events.function('volugraph.mapPlot.registerCoordCardSetter', (fn: unknown) => {
                this.coordCardSetLatLng = typeof fn === 'function' ? (fn as (la: number, lo: number) => void) : null;
            });

            try {
                this.events.removeFunction('volugraph.mapPlot.cardSetLatLng');
            } catch {
                /* ignore */
            }
            this.events.function('volugraph.mapPlot.cardSetLatLng', (p: { lat?: number; lng?: number }) => {
                if (!this.gcpMapPickArmed) return;
                if (
                    typeof p?.lat === 'number'
                    && typeof p?.lng === 'number'
                    && Number.isFinite(p.lat)
                    && Number.isFinite(p.lng)
                ) {
                    this.gcpMapPickArmed = false;
                    this.events.fire('volugraph.mapPlot.gcpMapPickArmed', false);
                    this.coordCardSetLatLng?.(p.lat, p.lng);
                }
            });

            try {
                this.events.removeFunction('volugraph.mapPlot.setGcpMapPickArmed');
            } catch {
                /* ignore */
            }
            this.events.function('volugraph.mapPlot.setGcpMapPickArmed', (armed: unknown) => {
                const on = armed === true;
                this.gcpMapPickArmed = on;
                this.events.fire('volugraph.mapPlot.gcpMapPickArmed', on);
            });

            try {
                this.events.removeFunction('volugraph.mapPlot.isGcpAddArmed');
            } catch {
                /* ignore */
            }
            this.events.function('volugraph.mapPlot.isGcpAddArmed', () => {
                return (
                    this.gcpAddArmed
                    && this.phase === 'gcps'
                    && this.pendingGcpDraft === null
                    && this.gcps.length < MAP_PLOT_MAX_GCP_COUNT
                );
            });

            try {
                this.events.removeFunction('volugraph.mapPlot.setGcpAddArmed');
            } catch {
                /* ignore */
            }
            this.events.function('volugraph.mapPlot.setGcpAddArmed', (armed: unknown) => {
                const v = armed === true;
                if (v && (this.phase !== 'gcps' || this.gcps.length >= MAP_PLOT_MAX_GCP_COUNT || this.pendingGcpDraft !== null)) {
                    return;
                }
                if (this.gcpAddArmed === v) return;
                this.gcpAddArmed = v;
                this.events.fire('volugraph.mapPlot.gcpAddArmed', v);
            });

            try {
                this.events.removeFunction('volugraph.mapPlot.notifyGcpDraftEnd');
            } catch {
                /* ignore */
            }
            this.events.function('volugraph.mapPlot.notifyGcpDraftEnd', () => {
                this.clearGcpDraftPending();
            });
        };
        reg();

        this.events.on(
            'volugraph.mapPlot.gcpDraftStarted',
            (payload: { n?: number; splat?: number[] }) => {
                if (this.phase !== 'gcps') return;
                const n = payload?.n;
                if (typeof n !== 'number' || !Number.isFinite(n)) return;
                this.pendingGcpDraft = { n };
                if (this.gcpAddArmed) {
                    this.gcpAddArmed = false;
                    this.events.fire('volugraph.mapPlot.gcpAddArmed', false);
                }
                const sp = payload?.splat;
                this.events.fire('volugraph.mapPlot.pendingGcp', {
                    n,
                    ...(Array.isArray(sp) && sp.length === 3 && sp.every(x => typeof x === 'number' && Number.isFinite(x))
                        ? { splat: [sp[0], sp[1], sp[2]] as [number, number, number] }
                        : {})
                });
            }
        );

        this.events.fire('volugraph.mapPlot.phase', { phase: 'closed' });
    }

    private clearGcpDraftPending(): void {
        this.pendingGcpDraft = null;
        this.coordCardSetLatLng = null;
        this.gcpMapPickArmed = false;
        this.events.fire('volugraph.mapPlot.gcpMapPickArmed', false);
        if (this.gcpAddArmed) {
            this.gcpAddArmed = false;
            this.events.fire('volugraph.mapPlot.gcpAddArmed', false);
        }
        this.events.fire('volugraph.mapPlot.pendingGcp', null);
        if (this.panel) {
            this.updatePanelUi();
        }
    }

    private exportState() {
        return {
            phase: this.phase,
            groundPlane: this.groundPlane,
            northBearingSplatDeg: this.getEffectiveNorthBearingDeg(),
            gcps: this.gcps.map(g => ({ ...g }))
        };
    }

    private getEffectiveNorthBearingDeg(): number {
        if (this.groundPlane && this.gcps.length >= 2) {
            const s = solveBearingFromGcps(this.groundPlane, this.gcps);
            if (s) return s.bearingDeg;
        }
        return this.northDeg;
    }

    private hydrateFromConfigIfClosed(): void {
        if (this.phase !== 'closed') return;
        const cfg = readMapPlotConfig();
        if (!cfg || cfg.alignmentSource !== 'workflow') return;
        const gp = cfg.groundPlane as { normal?: number[]; pointOnPlane?: number[] } | undefined;
        const nb = cfg.northBearingSplatDeg;
        const gcpsRaw = Array.isArray(cfg.gcps) ? cfg.gcps : [];
        if (
            gp?.normal?.length === 3 &&
            gp?.pointOnPlane?.length === 3 &&
            typeof nb === 'number' &&
            Number.isFinite(nb)
        ) {
            this.groundPlane = {
                normal: [Number(gp.normal[0]), Number(gp.normal[1]), Number(gp.normal[2])],
                pointOnPlane: [Number(gp.pointOnPlane[0]), Number(gp.pointOnPlane[1]), Number(gp.pointOnPlane[2])]
            };
            this.northDeg = nb;
            this.gcps = gcpsRaw
                .map((x: unknown, idx: number) => {
                    if (!x || typeof x !== 'object') return null;
                    const o = x as Record<string, unknown>;
                    const rawId = typeof o.id === 'string' ? o.id.trim() : '';
                    const id = rawId || `gcp-${idx}`;
                    const sp = o.splat;
                    if (!Array.isArray(sp) || sp.length !== 3) return null;
                    const lat = typeof o.lat === 'number' ? o.lat : NaN;
                    const lng = typeof o.lng === 'number' ? o.lng : NaN;
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                    const ele = typeof o.ele === 'number' && Number.isFinite(o.ele) ? o.ele : undefined;
                    return {
                        id,
                        splat: [Number(sp[0]), Number(sp[1]), Number(sp[2])] as [number, number, number],
                        lat,
                        lng,
                        ...(ele !== undefined ? { ele } : {})
                    };
                })
                .filter(Boolean) as GcpSnap[];

            if (this.groundPlane && this.gcps.length >= 2) {
                const s = solveBearingFromGcps(this.groundPlane, this.gcps);
                if (s) {
                    this.northDeg = s.bearingDeg;
                    this.bearingResidualDeg = s.residualDeg;
                }
            }
            this.syncAlignmentMath();
            this.pushNorthPreviewForUi();
        }
    }

    private syncAlignmentMath(): void {
        if (this.phase === 'plane') {
            this.lastFullSolve = null;
            return;
        }
        const solved = this.groundPlane && this.gcps.length >= 2 ? solveBearingFromGcps(this.groundPlane, this.gcps) : null;
        if (solved) {
            this.northDeg = solved.bearingDeg;
            this.bearingResidualDeg = solved.residualDeg;
        } else if (this.gcps.length < 2) {
            this.bearingResidualDeg = 0;
        }
        this.lastFullSolve = this.tryFullSolveForUi();
    }

    /**
     * Resume unfinished GIS alignment from persisted coord annotations (Firestore-backed):
     * usedAsGcp=true coord records become existing GCP pairs when no workflow gcps are loaded yet.
     */
    private hydrateGcpsFromCoordExportIfEmpty(): void {
        if (this.gcps.length > 0) return;
        const exported = this.events.invoke('coord.export') as unknown;
        if (!Array.isArray(exported) || exported.length === 0) return;
        const parsed = exported
            .map((item, idx) => {
                if (!item || typeof item !== 'object') return null;
                const o = item as Record<string, unknown>;
                if (o.kind !== 'coord' || o.usedAsGcp !== true) return null;
                const id = typeof o.id === 'string' ? o.id : '';
                const sp = o.position;
                if (!id || !Array.isArray(sp) || sp.length !== 3) return null;
                const sx = Number(sp[0]);
                const sy = Number(sp[1]);
                const sz = Number(sp[2]);
                if (!Number.isFinite(sx) || !Number.isFinite(sy) || !Number.isFinite(sz)) return null;
                const lat = typeof o.lat === 'number' ? o.lat : NaN;
                const lng = typeof o.lng === 'number' ? o.lng : NaN;
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                const ele = typeof o.ele === 'number' && Number.isFinite(o.ele) ? o.ele : undefined;
                const orderIndex =
                    typeof o.orderIndex === 'number' && Number.isFinite(o.orderIndex) ? o.orderIndex : Number.MAX_SAFE_INTEGER;
                return {
                    idx,
                    orderIndex,
                    gcp: {
                        id,
                        splat: [sx, sy, sz] as [number, number, number],
                        lat,
                        lng,
                        ...(ele !== undefined ? { ele } : {})
                    }
                };
            })
            .filter(Boolean) as Array<{ idx: number; orderIndex: number; gcp: GcpSnap }>;
        if (!parsed.length) return;
        parsed.sort((a, b) => {
            if (a.orderIndex !== b.orderIndex) return a.orderIndex - b.orderIndex;
            return a.idx - b.idx;
        });
        this.gcps = parsed.map((x) => x.gcp);
        this.nextGcpNumber = this.gcps.length + 1;
    }

    private tryFullSolveForUi(): FullSolveUi | null {
        if (!this.groundPlane || this.gcps.length < MAP_PLOT_MIN_GCP_TO_SOLVE) return null;
        const fn = this.events.functions.get('volugraph.mapPlot.fullSolveForUi') as
            | ((x: {
                  groundPlane: { normal: number[]; pointOnPlane: number[] };
                  northBearingSplatDeg: number;
                  gcps: { splat: number[]; lat: number; lng: number; ele?: number }[];
              }) => {
                  scale: number;
                  residualMeters: number;
                  gcpResidualsMeters: number[];
                  headingDeg: number;
                  basisNorth?: number[];
                  basisUp?: number[];
                  lat?: number;
                  lng?: number;
              } | null)
            | undefined;
        if (typeof fn !== 'function') return null;
        try {
            const out = fn({
                groundPlane: this.groundPlane,
                northBearingSplatDeg: this.getEffectiveNorthBearingDeg(),
                gcps: this.gcps.map(g => ({
                    splat: [...g.splat],
                    lat: g.lat,
                    lng: g.lng,
                    ...(typeof g.ele === 'number' && Number.isFinite(g.ele) ? { ele: g.ele } : {})
                }))
            });
            if (
                out
                && typeof (out as { scale?: unknown }).scale === 'number'
                && Number.isFinite((out as { scale: number }).scale)
                && typeof (out as { residualMeters?: unknown }).residualMeters === 'number'
                && Number.isFinite((out as { residualMeters: number }).residualMeters)
                && Array.isArray((out as { gcpResidualsMeters?: unknown }).gcpResidualsMeters)
            ) {
                const o = out as {
                    scale: number;
                    residualMeters: number;
                    gcpResidualsMeters: number[];
                    headingDeg?: number;
                    basisNorth?: unknown;
                    basisUp?: unknown;
                    lat?: number;
                    lng?: number;
                };
                const base: FullSolveUi = {
                    scale: o.scale,
                    residualMeters: o.residualMeters,
                    gcpResidualsMeters: o.gcpResidualsMeters.filter((x): x is number => typeof x === 'number' && Number.isFinite(x)),
                    headingDeg:
                        typeof o.headingDeg === 'number' && Number.isFinite(o.headingDeg) ? o.headingDeg : this.northDeg
                };
                if (
                    Array.isArray(o.basisNorth)
                    && o.basisNorth.length === 3
                    && o.basisNorth.every((x): x is number => typeof x === 'number' && Number.isFinite(x))
                ) {
                    base.basisNorth = [...o.basisNorth];
                }
                if (
                    Array.isArray(o.basisUp)
                    && o.basisUp.length === 3
                    && o.basisUp.every((x): x is number => typeof x === 'number' && Number.isFinite(x))
                ) {
                    base.basisUp = [...o.basisUp];
                }
                if (typeof o.lat === 'number' && Number.isFinite(o.lat)) base.lat = o.lat;
                if (typeof o.lng === 'number' && Number.isFinite(o.lng)) base.lng = o.lng;
                return base;
            }
        } catch {
            /* ignore */
        }
        return null;
    }

    private toggle(): void {
        if (this.phase === 'closed') {
            this.hydrateFromConfigIfClosed();
            this.open();
        } else {
            this.close();
        }
    }

    private open(): void {
        this.events.fire('tool.deactivate');
        this.buildPanel();
        window.addEventListener('resize', this.boundLayoutMapPlotPanel);

        if (!this.groundPlane) {
            this.phase = 'plane';
            this.gizmo = new MapPlotGroundPlaneGizmo(this.scene);
            this.gizmo.activatePlaneGizmos('translate');
            this.syncPlaneGizmoModeButtons('translate');
        } else {
            this.phase = 'gcps';
            this.showStep('gcps');
            this.armGcpPhase();
        }

        if (this.gizmo && this.groundPlane) {
            const p = this.groundPlane.pointOnPlane;
            const n = this.groundPlane.normal;
            this.gizmo.setCenter(p[0], p[1], p[2]);
            this.gizmo.setNormal(n[0], n[1], n[2]);
        }

        this.updatePanelUi();
        this.layoutMapPlotPanel();
        this.fireMapPlotPhase();
        requestAnimationFrame(() => this.layoutMapPlotPanel());
    }

    private fireMapPlotPhase(): void {
        if (this.phase === 'closed') {
            this.events.fire('volugraph.mapPlot.phase', { phase: 'closed' });
            this.pushHostState();
        } else {
            this.events.fire('volugraph.mapPlot.phase', { phase: this.phase });
        }
    }

    /** Host UI: satellite overlay + confidence chips (GCP count, bearing residual, solver payloads). */
    private pushHostState(): void {
        const solved =
            this.groundPlane && this.gcps.length >= 2 ? solveBearingFromGcps(this.groundPlane, this.gcps) : null;
        const fs = this.lastFullSolve;
        this.events.fire('volugraph.mapPlot.state', {
            phase: this.phase,
            gcpCount: this.gcps.length,
            hasGroundPlane: !!this.groundPlane,
            bearingResidualDeg: solved ? solved.residualDeg : this.bearingResidualDeg,
            bearingSolved: !!solved,
            groundPlane: this.groundPlane,
            northBearingSplatDeg: this.getEffectiveNorthBearingDeg(),
            gcps: this.gcps.map(g => ({ ...g })),
            ...(fs
                ? {
                      residualMeters: fs.residualMeters,
                      scale: fs.scale,
                      headingDeg: fs.headingDeg,
                      gcpResidualsMeters: fs.gcpResidualsMeters
                  }
                : {})
        });
    }

    /**
     * Compass / N-chevron: north from GCPs is “known” when ≥2 GCPs yield a bearing solve.
     * During ground-plane step we hide the chevron (user may still be editing the plane).
     * When map plot is closed, keep showing solved bearing from persisted session so the normal viewer stays aligned.
     */
    private pushNorthPreviewForUi(): void {
        if (this.phase === 'plane') {
            this.events.fire('volugraph.mapPlot.northPreview', {
                deg: this.northDeg,
                residualDeg: this.bearingResidualDeg,
                known: false
            });
            return;
        }
        const fs = this.lastFullSolve;
        const solved2 = this.groundPlane && this.gcps.length >= 2 ? solveBearingFromGcps(this.groundPlane, this.gcps) : null;
        const known = !!solved2 || !!fs;
        const degForBrg = fs && Number.isFinite(fs.headingDeg) ? fs.headingDeg : this.northDeg;
        const basisNorth =
            fs?.basisNorth && fs.basisNorth.length === 3
                ? fs.basisNorth
                : solved2?.basisNorth;
        const basisUp =
            fs?.basisUp && fs.basisUp.length === 3
                ? fs.basisUp
                : solved2?.basisUp;
        this.events.fire('volugraph.mapPlot.northPreview', {
            deg: degForBrg,
            residualDeg: solved2 ? solved2.residualDeg : this.bearingResidualDeg,
            known,
            ...(basisNorth ? { basisNorth } : {}),
            ...(basisUp ? { basisUp } : {}),
            ...(fs
                ? {
                      scale: fs.scale,
                      residualMeters: fs.residualMeters,
                      gcpResidualsMeters: fs.gcpResidualsMeters
                  }
                : {})
        });
    }

    private recomputeBearingAfterGcpChange(): void {
        this.updatePanelUi();
    }

    private layoutMapPlotPanel(): void {
        if (!this.panel) return;

        const floatingSplit =
            this.phase === 'gcps' &&
            typeof document !== 'undefined' &&
            document.body.classList.contains('volugraph-gcp-map-left-panel');
        this.panel.classList.toggle('volugraph-map-plot-panel--floating', floatingSplit);

        if (!floatingSplit && typeof document !== 'undefined') {
            document.body.style.removeProperty('--volugraph-gcp-modal-bottom');
            document.body.style.removeProperty('--volugraph-gcp-modal-width');
            document.body.style.removeProperty('--volugraph-gcp-modal-left');
        }

        const titleStack = document.querySelector('.capture-viewer-overlay-title-stack');
        const titleCard = document.querySelector('.capture-viewer-overlay-title-card');
        const titleEl =
            document.getElementById('volugraph-studio-title-block') ??
            document.querySelector('.studio-title-block');
        const anchor = titleStack ?? titleCard ?? titleEl;
        const gap = 10;
        const edgePad = 12;
        const { style } = this.panel;
        const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
        const vh = typeof window !== 'undefined' ? window.innerHeight : 768;

        style.position = 'fixed';
        /* Above workspace chrome; stacked under title via JS; transform reserved for open transition in CSS */
        style.zIndex = '12001';
        style.right = 'auto';

        const roomRight = vw - edgePad;
        const maxPanelW = Math.min(520, vw - 2 * edgePad);

        const clampPanelLeft = () => {
            if (!this.panel) return;
            const w = this.panel.getBoundingClientRect().width;
            let leftN = parseFloat(this.panel.style.left || '0') || 0;
            if (leftN + w > roomRight) {
                leftN = Math.max(edgePad, roomRight - w);
                this.panel.style.left = `${Math.round(leftN)}px`;
            }
        };

        const afterLayout = () => {
            if (floatingSplit) {
                return;
            }
            clampPanelLeft();
            this.syncMapPlotTrackSummaryWrap();
        };

        if (floatingSplit) {
            style.top = '';
            style.position = 'fixed';
            style.zIndex = '12012';
            style.right = 'auto';
            style.width = '';
            style.maxWidth = '';
            style.minWidth = '280px';

            const tsEl = titleStack as HTMLElement | null;
            const ar = tsEl?.getBoundingClientRect();
            const panelW = Math.min(vw * 0.42, 540);
            const stripPad = 8;
            let leftPx = ar ? Math.round(ar.left) : stripPad;
            leftPx = Math.max(edgePad, Math.min(leftPx, Math.max(edgePad, vw - panelW - edgePad)));
            const topPx = ar ? Math.round(ar.bottom + gap) : Math.round(edgePad + 56);
            style.left = `${leftPx}px`;
            style.top = `${topPx}px`;
            style.width = `${Math.round(panelW)}px`;

            requestAnimationFrame(() => {
                this.syncMapPlotTrackSummaryWrap();
                if (!this.panel || typeof document === 'undefined') return;

                const syncMapSplitVarsFromPanel = (): void => {
                    const brNow = this.panel!.getBoundingClientRect();
                    const host = document.getElementById('volugraph-map-plot-host');
                    let leftForHost = brNow.left;
                    let topEdgeBelowModal = brNow.bottom;
                    if (host) {
                        const hr = host.getBoundingClientRect();
                        leftForHost = brNow.left - hr.left;
                        topEdgeBelowModal = brNow.bottom - hr.top;
                    }
                    document.body.style.setProperty(
                        '--volugraph-gcp-modal-bottom',
                        `${Math.round(topEdgeBelowModal)}px`
                    );
                    document.body.style.setProperty('--volugraph-gcp-modal-width', `${Math.round(brNow.width)}px`);
                    document.body.style.setProperty('--volugraph-gcp-modal-left', `${Math.round(leftForHost)}px`);
                };

                syncMapSplitVarsFromPanel();
                const pr = this.panel.getBoundingClientRect();
                if (pr.right > vw - edgePad) {
                    const nextLeft = Math.max(edgePad, vw - edgePad - pr.width);
                    this.panel!.style.left = `${Math.round(nextLeft)}px`;
                    syncMapSplitVarsFromPanel();
                }
            });
            return;
        }

        if (anchor) {
            const ar = anchor.getBoundingClientRect();
            const leftRaw = Math.round(ar.left);
            const left = Math.max(edgePad, leftRaw);
            style.top = `${Math.round(ar.bottom + gap)}px`;
            style.left = `${left}px`;
            style.width = 'fit-content';
            style.maxWidth = `${maxPanelW}px`;
            style.minWidth = '280px';
            const panelH = this.panel.offsetHeight || 400;
            let topNum = Math.round(ar.bottom + gap);
            if (topNum + panelH > vh - edgePad) {
                topNum = Math.max(edgePad, vh - edgePad - panelH);
                style.top = `${topNum}px`;
            }
            requestAnimationFrame(afterLayout);
        } else {
            style.top = '56px';
            style.left = `${edgePad}px`;
            style.width = 'fit-content';
            style.maxWidth = `${maxPanelW}px`;
            style.minWidth = '280px';
            requestAnimationFrame(afterLayout);
        }
    }

    /** Constrain intro copy width to the step row (Ground plane → GCP) so wrapping aligns with the chips. */
    private syncMapPlotTrackSummaryWrap(): void {
        if (!this.panel) return;
        const list = this.panel.querySelector('.volugraph-map-plot-track__list') as HTMLElement | null;
        const summary = this.panel.querySelector('.volugraph-map-plot-track__summary') as HTMLElement | null;
        if (!list || !summary) return;
        const w = list.scrollWidth;
        if (w > 0) {
            summary.style.maxWidth = `${Math.ceil(w)}px`;
        }
    }

    private syncStepTrack(): void {
        if (!this.panel || this.phase === 'closed') return;

        const doneFor = (key: string): boolean => {
            if (key === 'plane') return !!this.groundPlane;
            if (key === 'gcps') return this.gcps.length >= MAP_PLOT_MIN_GCP_TO_SOLVE;
            return false;
        };
        const currentKey = this.phase === 'plane' ? 'plane' : 'gcps';

        this.panel.querySelectorAll('[data-track-step]').forEach((el) => {
            const key = el.getAttribute('data-track-step');
            if (!key) return;
            el.classList.remove(
                'volugraph-map-plot-track__item--done',
                'volugraph-map-plot-track__item--current',
                'volugraph-map-plot-track__item--pending'
            );
            const done = doneFor(key);
            const current = key === currentKey;
            if (current) {
                el.classList.add('volugraph-map-plot-track__item--current');
            } else if (done) {
                el.classList.add('volugraph-map-plot-track__item--done');
            } else {
                el.classList.add('volugraph-map-plot-track__item--pending');
            }
        });
    }

    private syncPlaneGizmoModeButtons(mode: MapPlotPlaneGizmoMode): void {
        if (!this.panel) return;
        const seg = this.panel.querySelector('.volugraph-map-plot-gizmo-segmented');
        seg?.classList.toggle('volugraph-map-plot-gizmo-segmented--translate', mode === 'translate');
        seg?.classList.toggle('volugraph-map-plot-gizmo-segmented--rotate', mode === 'rotate');
        this.panel.querySelectorAll('[data-plane-mode]').forEach((btn) => {
            const m = btn.getAttribute('data-plane-mode');
            const on = m === mode;
            (btn as HTMLButtonElement).setAttribute('aria-selected', on ? 'true' : 'false');
        });
    }

    private close(): void {
        this.clearGcpDraftPending();
        this.phase = 'closed';
        this.teardownGcpListener();
        this.restoreCoordHooks();
        window.removeEventListener('resize', this.boundLayoutMapPlotPanel);
        if (this.gizmo) {
            this.gizmo.destroy();
            this.gizmo = null;
        }
        if (this.panelRoot) {
            try {
                this.panelRoot.remove();
            } catch {
                /* ignore */
            }
            this.panelRoot = null;
            this.panel = null;
        } else if (this.panel) {
            this.panel.remove();
            this.panel = null;
        }
        if (typeof document !== 'undefined') {
            document.body.style.removeProperty('--volugraph-gcp-modal-bottom');
            document.body.style.removeProperty('--volugraph-gcp-modal-width');
            document.body.style.removeProperty('--volugraph-gcp-modal-left');
        }
        this.events.fire('tool.deactivate');
        this.fireMapPlotPhase();
        this.syncAlignmentMath();
        this.pushNorthPreviewForUi();
    }

    private restoreCoordHooks(): void {
        try {
            this.events.removeFunction('coord.entryMode');
        } catch {
            /* ignore */
        }
        this.events.function('coord.entryMode', () => 'modal');
        try {
            this.events.removeFunction('coord.extraFieldsForNewMarker');
        } catch {
            /* ignore */
        }
        this.events.function('coord.extraFieldsForNewMarker', () => ({}));
    }

    private armGcpPhase(): void {
        this.hydrateGcpsFromCoordExportIfEmpty();
        this.nextGcpNumber = this.gcps.length + 1;
        this.clearGcpDraftPending();
        this.teardownGcpListener();
        try {
            this.events.removeFunction('coord.entryMode');
        } catch {
            /* ignore */
        }
        this.events.function('coord.entryMode', () => 'hover');
        try {
            this.events.removeFunction('coord.extraFieldsForNewMarker');
        } catch {
            /* ignore */
        }
        this.events.function('coord.extraFieldsForNewMarker', () => ({ usedAsGcp: true }));

        const handle = (payload: {
            action?: string;
            id?: string;
            record?: { id?: string; kind?: string; position?: number[]; lat?: number; lng?: number; ele?: number };
        }) => {
            if (this.phase !== 'gcps') return;
            if (payload?.action === 'delete' && typeof payload.id === 'string') {
                this.gcps = this.gcps.filter(g => g.id !== payload.id);
                this.nextGcpNumber = this.gcps.length + 1;
                this.recomputeBearingAfterGcpChange();
                this.updatePanelUi();
                return;
            }
            if (payload?.action !== 'upsert' || !payload.record) return;
            const r = payload.record;
            if (r.kind !== 'coord') return;
            if (typeof r.id !== 'string' || !Array.isArray(r.position) || r.position.length !== 3) return;
            if (typeof r.lat !== 'number' || typeof r.lng !== 'number') return;
            const snap: GcpSnap = {
                id: r.id,
                splat: [r.position[0], r.position[1], r.position[2]],
                lat: r.lat,
                lng: r.lng,
                ...(typeof r.ele === 'number' && Number.isFinite(r.ele) ? { ele: r.ele } : {})
            };
            const idx = this.gcps.findIndex(g => g.id === snap.id);
            if (idx >= 0) this.gcps[idx] = snap;
            else this.gcps.push(snap);
            this.nextGcpNumber = this.gcps.length + 1;
            this.recomputeBearingAfterGcpChange();
            this.updatePanelUi();
        };
        const sub = this.events.on('volugraph.rfi', handle);
        this.unsubRfi = () => {
            try {
                sub?.off?.();
            } catch {
                /* ignore */
            }
        };
        this.events.fire('tool.coord');
    }

    private teardownGcpListener(): void {
        if (this.unsubRfi) {
            this.unsubRfi();
            this.unsubRfi = null;
        }
    }

    private resetGisAlignment(): void {
        try {
            const fn = this.events.functions.get('coord.clearAlignmentGcpMarkers') as (() => unknown) | undefined;
            if (typeof fn === 'function') {
                fn();
            }
        } catch {
            /* ignore */
        }

        this.events.fire('volugraph.mapPlot.clearPersistedAlignment');

        this.groundPlane = null;
        this.gcps = [];
        this.northDeg = 0;
        this.bearingResidualDeg = 0;
        this.lastFullSolve = null;
        this.nextGcpNumber = 1;
        this.clearGcpDraftPending();
        this.teardownGcpListener();
        this.restoreCoordHooks();

        if (this.gizmo) {
            try {
                this.gizmo.destroy();
            } catch {
                /* ignore */
            }
            this.gizmo = null;
        }

        if (this.panel) {
            this.phase = 'plane';
            this.showStep('plane');
            this.gizmo = new MapPlotGroundPlaneGizmo(this.scene);
            this.gizmo.activatePlaneGizmos('translate');
            this.syncPlaneGizmoModeButtons('translate');
            this.updatePanelUi();
            this.fireMapPlotPhase();
            this.pushNorthPreviewForUi();
            this.pushHostState();
            this.layoutMapPlotPanel();
            requestAnimationFrame(() => {
                this.scene.forceRender = true;
            });
        } else {
            this.phase = 'closed';
            this.syncAlignmentMath();
            this.pushNorthPreviewForUi();
            this.pushHostState();
        }
    }

    private goBackToPlane(): void {
        if (!this.groundPlane) return;
        this.clearGcpDraftPending();
        this.teardownGcpListener();
        this.restoreCoordHooks();
        this.phase = 'plane';
        this.showStep('plane');
        if (this.gizmo) {
            this.gizmo.destroy();
            this.gizmo = null;
        }
        this.gizmo = new MapPlotGroundPlaneGizmo(this.scene);
        const p = this.groundPlane.pointOnPlane;
        const n = this.groundPlane.normal;
        this.gizmo.setCenter(p[0], p[1], p[2]);
        this.gizmo.setNormal(n[0], n[1], n[2]);
        this.gizmo.activatePlaneGizmos('translate');
        this.syncPlaneGizmoModeButtons('translate');
        this.updatePanelUi();
        this.fireMapPlotPhase();
        this.pushNorthPreviewForUi();
        this.layoutMapPlotPanel();
        requestAnimationFrame(() => {
            this.scene.forceRender = true;
        });
    }

    private buildPanel(): void {
        if (this.panelRoot) {
            try {
                this.panelRoot.remove();
            } catch {
                /* ignore */
            }
            this.panelRoot = null;
            this.panel = null;
        } else if (this.panel) {
            this.panel.remove();
            this.panel = null;
        }

        const portal = document.createElement('div');
        portal.className = 'volugraph-map-plot-panel-root';

        /* `data-volugraph-supersplat` on a full-bleed node applies embed #1b242e background — hide the splat.
         * Scope with display:contents so panel descendants still match [data-volugraph-supersplat] .volugraph-map-plot-panel. */
        const scope = document.createElement('div');
        scope.setAttribute('data-volugraph-supersplat', '');
        scope.className = 'volugraph-map-plot-panel-scope';

        const panel = document.createElement('div');
        panel.className = 'volugraph-map-plot-panel';
        panel.innerHTML = `
<div class="volugraph-map-plot-panel__head">
  <span class="volugraph-map-plot-panel__title">MAP PLOT ALIGNMENT</span>
  <button type="button" class="volugraph-map-plot-panel__close" aria-label="Close">&times;</button>
</div>
<div class="volugraph-map-plot-track" aria-label="Alignment workflow steps">
  <p class="volugraph-map-plot-track__summary">Complete alignment steps in order to enable map plotting for this capture.</p>
  <ol class="volugraph-map-plot-track__list" role="list">
    <li class="volugraph-map-plot-track__item" data-track-step="plane"><span class="volugraph-map-plot-track__n">1</span><span class="volugraph-map-plot-track__sep" aria-hidden="true"> \u00B7 </span><span class="volugraph-map-plot-track__name">Ground plane</span></li>
    <li class="volugraph-map-plot-track__arrow" aria-hidden="true"><span class="volugraph-map-plot-track__arrow-glyph">\u2192</span></li>
    <li class="volugraph-map-plot-track__item" data-track-step="gcps"><span class="volugraph-map-plot-track__n">2</span><span class="volugraph-map-plot-track__sep" aria-hidden="true"> \u00B7 </span><span class="volugraph-map-plot-track__name">GROUND CONTROL POINT</span></li>
    <li class="volugraph-map-plot-track__reset-wrap">
      <button type="button" class="volugraph-map-plot-track__reset" data-reset-gis aria-label="Reset alignment" title="Reset alignment">
        <svg class="volugraph-map-plot-track__reset-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M240,56v48a8,8,0,0,1-8,8H184a8,8,0,0,1,0-16H211.4L201,77.86A88,88,0,0,0,56.38,72.87,8,8,0,0,1,42.68,68.6,104,104,0,0,1,204.34,64.33L224,84V56a8,8,0,0,1,16,0ZM42.68,187.4a8,8,0,0,1,13.7-4.27,88,88,0,0,0,144.94-4.99L192,172h27.39a8,8,0,0,1,0,16H171.39a8,8,0,0,1-8-8V132.61a8,8,0,0,1,16,0V160.6l20.36-20.36A104,104,0,0,1,42.68,187.4Z"/></svg>
      </button>
    </li>
  </ol>
</div>
<div class="volugraph-map-plot-panel__steps">
  <div class="volugraph-map-plot-step" data-step="plane">
    <div class="volugraph-map-plot-step__row">
      <span class="volugraph-map-plot-step__tick">1</span>
      <span class="volugraph-map-plot-step__label">GROUND PLANE</span>
    </div>
    <div class="volugraph-map-plot-step__body">
      <div class="volugraph-map-plot-gizmo-segmented volugraph-map-plot-gizmo-segmented--translate" role="tablist" aria-label="Plane gizmo">
        <span class="volugraph-map-plot-gizmo-segmented__thumb" aria-hidden="true"></span>
        <button type="button" role="tab" class="volugraph-map-plot-gizmo-tab" data-plane-mode="translate" aria-selected="true">Move</button>
        <button type="button" role="tab" class="volugraph-map-plot-gizmo-tab" data-plane-mode="rotate" aria-selected="false">Tilt</button>
      </div>
      <button type="button" class="annotation-modal-btn volugraph-map-plot-btn--confirm-white" data-confirm-plane>CONFIRM PLANE</button>
    </div>
  </div>
  <div class="volugraph-map-plot-step" data-step="gcps" hidden>
    <div class="volugraph-map-plot-step__body">
      <div class="volugraph-map-plot-gcp-meta" aria-live="polite">
        <p class="volugraph-map-plot-gcp-summary">Ground control points: <span data-gcp-count>0</span> / ${MAP_PLOT_MAX_GCP_COUNT} (add at least ${MAP_PLOT_MIN_GCP_TO_SOLVE} to align)</p>
          <div class="volugraph-map-plot-fit-review" data-fit-review hidden>
          <p class="volugraph-map-plot-fit-review__row"><span class="volugraph-map-plot-fit-review__k">Scale (1 world unit)</span> <span class="volugraph-map-plot-fit-review__v" data-fit-scale>—</span></p>
          <p class="volugraph-map-plot-fit-review__row"><span class="volugraph-map-plot-fit-review__k">RMS residual</span> <span class="volugraph-map-plot-fit-review__v" data-fit-rms>—</span></p>
          <ul class="volugraph-map-plot-fit-review__gcp-list" data-fit-gcp-list></ul>
        </div>
      </div>
      <div class="volugraph-map-plot-step__actions-row">
        <button type="button" class="annotation-modal-btn volugraph-map-plot-btn--back" data-back-plane>Back to step 1</button>
        <button type="button" class="annotation-modal-btn volugraph-map-plot-btn--add-gcp" data-add-gcp aria-pressed="false"><span class="volugraph-map-plot-add-gcp-glyph" aria-hidden="true">+</span> ADD GCP</button>
        <button type="button" class="annotation-modal-btn annotation-modal-btn-primary" data-save-alignment disabled title="">Save alignment</button>
      </div>
    </div>
  </div>
</div>`;
        scope.appendChild(panel);
        portal.appendChild(scope);
        const mapPlotHost =
            typeof document !== 'undefined' ? document.getElementById('volugraph-map-plot-host') : null;
        (mapPlotHost ?? document.body).appendChild(portal);
        this.panelRoot = portal;
        this.panel = panel;

        const prefersReducedMotion =
            typeof window !== 'undefined' &&
            window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
        if (prefersReducedMotion) {
            panel.classList.add('volugraph-map-plot-panel--shown');
        } else {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    panel.classList.add('volugraph-map-plot-panel--shown');
                });
            });
        }

        const closeBtn = panel.querySelector('.volugraph-map-plot-panel__close');
        closeBtn?.addEventListener('click', () => this.close());

        panel.querySelector('[data-reset-gis]')?.addEventListener('click', () => {
            if (
                typeof window !== 'undefined'
                && !window.confirm('Clear saved GIS alignment for this capture and remove all GCP coordinate markers?')
            ) {
                return;
            }
            this.resetGisAlignment();
        });

        panel.querySelectorAll('[data-plane-mode]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const m = btn.getAttribute('data-plane-mode');
                if (m !== 'translate' && m !== 'rotate') return;
                this.gizmo?.setPlaneGizmoMode(m);
                this.syncPlaneGizmoModeButtons(m);
                requestAnimationFrame(() => {
                    this.scene.forceRender = true;
                });
            });
        });

        panel.querySelector('[data-confirm-plane]')?.addEventListener('click', () => {
            if (!this.gizmo) return;
            this.gizmo.deactivatePlaneGizmos();
            this.groundPlane = this.gizmo.getGroundPlane();
            if (this.gizmo) {
                this.gizmo.destroy();
                this.gizmo = null;
            }
            this.phase = 'gcps';
            this.showStep('gcps');
            this.armGcpPhase();
            // Defer React state pushes so browser can paint the GCP panel step first,
            // preventing the google.maps.Map init from blocking the UI update.
            requestAnimationFrame(() => {
                this.recomputeBearingAfterGcpChange();
                this.updatePanelUi();
                this.fireMapPlotPhase();
            });
        });

        panel.querySelector('[data-back-plane]')?.addEventListener('click', () => this.goBackToPlane());

        panel.querySelector('[data-add-gcp]')?.addEventListener('click', () => {
            const canArm =
                this.phase === 'gcps' && this.gcps.length < MAP_PLOT_MAX_GCP_COUNT && this.pendingGcpDraft === null;
            if (!canArm) return;
            const next = !this.gcpAddArmed;
            if (this.gcpAddArmed === next) return;
            this.gcpAddArmed = next;
            this.events.fire('volugraph.mapPlot.gcpAddArmed', next);
            this.updatePanelUi();
        });

        panel.querySelector('[data-save-alignment]')?.addEventListener('click', () => {
            if (!this.groundPlane || this.gcps.length < MAP_PLOT_MIN_GCP_TO_SOLVE) return;
            const fs = this.lastFullSolve;
            const RMS_BLOCK = 10;
            if (fs && fs.residualMeters > RMS_BLOCK) {
                const ok = typeof window !== 'undefined'
                    && window.confirm(
                        `RMS residual is ${fs.residualMeters.toFixed(2)} m — significantly above the ${RMS_BLOCK} m threshold.\n` +
                        'Consider repositioning GCPs or adding more control points.\n\nSave anyway?'
                    );
                if (!ok) return;
            }
            const cfg = readMapPlotConfig();
            let prevOpacity =
                cfg && typeof (cfg as { opacity?: unknown }).opacity === 'number' && Number.isFinite((cfg as { opacity: number }).opacity) ?
                    Math.min(1, Math.max(0, (cfg as { opacity: number }).opacity)) :
                    1;
            const draftFn = this.events.functions.get('volugraph.mapPlot.draftOpacity');
            if (typeof draftFn === 'function') {
                const d = draftFn();
                if (typeof d === 'number' && Number.isFinite(d)) {
                    prevOpacity = Math.min(1, Math.max(0, d));
                }
            }
            this.events.fire('volugraph.mapPlot.save', {
                groundPlane: this.groundPlane,
                northBearingSplatDeg: this.getEffectiveNorthBearingDeg(),
                gcps: this.gcps.map(g => ({ ...g })),
                opacity: prevOpacity
            });
            this.close();
        });

        this.layoutMapPlotPanel();
    }

    private showStep(name: 'plane' | 'gcps'): void {
        if (!this.panel) return;
        for (const s of ['plane', 'gcps'] as const) {
            const el = this.panel.querySelector(`[data-step="${s}"]`) as HTMLElement | null;
            if (!el) continue;
            el.hidden = s !== name;
        }
        this.syncStepTrack();
        this.layoutMapPlotPanel();
    }

    private updatePanelUi(): void {
        if (!this.panel) return;
        if (this.phase === 'gcps') {
            this.syncAlignmentMath();
        }

        const gcpCount = this.panel.querySelector('[data-gcp-count]');
        if (gcpCount) gcpCount.textContent = String(this.gcps.length);

        const fs = this.lastFullSolve;

        const fitReview = this.panel.querySelector<HTMLElement>('[data-fit-review]');
        const fitScale = this.panel.querySelector('[data-fit-scale]');
        const fitRms = this.panel.querySelector('[data-fit-rms]');
        const fitList = this.panel.querySelector('[data-fit-gcp-list]');
        const RMS_OK = readMapPlotRmsOkM();
        const RMS_AUTHORITATIVE = 5;
        const RMS_BLOCK = 10;
        if (fitReview && fitScale && fitRms && fitList) {
            if (fs && this.gcps.length >= MAP_PLOT_MIN_GCP_TO_SOLVE) {
                fitReview.hidden = false;
                fitScale.textContent = `≈ ${fs.scale.toFixed(4)} m`;
                fitRms.textContent = `± ${fs.residualMeters.toFixed(2)} m`;
                fitRms.classList.toggle('volugraph-map-plot-fit-review__v--ok',    fs.residualMeters <= RMS_OK);
                fitRms.classList.toggle('volugraph-map-plot-fit-review__v--warn',  fs.residualMeters > RMS_OK && fs.residualMeters <= RMS_AUTHORITATIVE);
                fitRms.classList.toggle('volugraph-map-plot-fit-review__v--error', fs.residualMeters > RMS_AUTHORITATIVE);

                // Per-GCP residuals with outlier highlighting (> 3× median = red badge)
                const sorted = [...fs.gcpResidualsMeters].sort((a, b) => a - b);
                const mid = Math.floor(sorted.length / 2);
                const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
                const outlierThreshold = median > 0 ? median * 3 : Infinity;

                fitList.innerHTML = '';
                fs.gcpResidualsMeters.forEach((m, i) => {
                    const li = document.createElement('li');
                    li.textContent = `GCP ${i + 1} · ${m.toFixed(2)} m`;
                    if (m > outlierThreshold) {
                        li.classList.add('volugraph-map-plot-fit-review__gcp--outlier');
                    }
                    fitList.appendChild(li);
                });
            } else {
                fitReview.hidden = true;
                fitScale.textContent = '—';
                fitRms.textContent = '—';
                fitRms.classList.remove(
                    'volugraph-map-plot-fit-review__v--ok',
                    'volugraph-map-plot-fit-review__v--warn',
                    'volugraph-map-plot-fit-review__v--error'
                );
                fitList.innerHTML = '';
            }
        }

        const addGcpBtn = this.panel.querySelector('[data-add-gcp]') as HTMLButtonElement | null;
        if (addGcpBtn) {
            const canAdd =
                this.phase === 'gcps' && this.gcps.length < MAP_PLOT_MAX_GCP_COUNT && this.pendingGcpDraft === null;
            addGcpBtn.disabled = !canAdd;
            addGcpBtn.setAttribute('aria-pressed', this.gcpAddArmed ? 'true' : 'false');
            addGcpBtn.classList.toggle('volugraph-map-plot-btn--add-gcp--armed', this.gcpAddArmed);
            if (this.gcps.length >= MAP_PLOT_MAX_GCP_COUNT) {
                addGcpBtn.title = `Maximum ${MAP_PLOT_MAX_GCP_COUNT} control points for this alignment. Remove one to add another.`;
            } else {
                addGcpBtn.title = '';
            }
            if (!canAdd && this.gcpAddArmed) {
                this.gcpAddArmed = false;
                this.events.fire('volugraph.mapPlot.gcpAddArmed', false);
                addGcpBtn.setAttribute('aria-pressed', 'false');
                addGcpBtn.classList.remove('volugraph-map-plot-btn--add-gcp--armed');
            }
        }

        const saveBtn = this.panel.querySelector('[data-save-alignment]') as HTMLButtonElement | null;
        if (saveBtn) {
            const ok = this.phase === 'gcps' && this.gcps.length >= MAP_PLOT_MIN_GCP_TO_SOLVE && !!this.groundPlane;
            saveBtn.disabled = !ok;
            if (fs && fs.residualMeters > RMS_BLOCK) {
                saveBtn.classList.add('volugraph-map-plot-save--warn', 'volugraph-map-plot-save--block');
                saveBtn.title = `High RMS (${fs.residualMeters.toFixed(2)} m) — add more GCPs or reposition existing ones. Save will ask for confirmation.`;
            } else if (fs && fs.residualMeters > RMS_OK) {
                saveBtn.classList.add('volugraph-map-plot-save--warn');
                saveBtn.classList.remove('volugraph-map-plot-save--block');
                saveBtn.title = `Add another GCP — RMS is ${fs.residualMeters.toFixed(2)} m (target ≤ ${RMS_OK} m). You can still save.`;
            } else {
                saveBtn.classList.remove('volugraph-map-plot-save--warn', 'volugraph-map-plot-save--block');
                saveBtn.title = '';
            }
        }
        for (const step of Array.from(this.panel.querySelectorAll('[data-step]'))) {
            const name = step.getAttribute('data-step');
            let done = false;
            if (name === 'plane') done = !!this.groundPlane;
            if (name === 'gcps') done = this.gcps.length >= MAP_PLOT_MIN_GCP_TO_SOLVE;
            const tick = step.querySelector('.volugraph-map-plot-step__tick');
            if (tick) {
                tick.classList.toggle('volugraph-map-plot-step__tick--done', done);
            }
        }
        this.syncStepTrack();
        this.layoutMapPlotPanel();
        this.pushHostState();
        this.pushNorthPreviewForUi();
    }
}
